import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { DeviceDetectorService } from 'ngx-device-detector';
import { FIELD_TYPES } from '../../../../../shared/fields/field-types';
import { STANDARD_FIELDS } from '../../../../../shared/fields/standard-fields';
import { Field } from '../../../core/store/models';
import { PDropdownOption } from '../../interfaces/p-dropdown-options';
import { CURRENCY_MISMATCH_TOOLTIP } from '../../constants/constants';

export enum FormPosition {
    Left,
    Right,
    Third,
    Fourth,
    Fifth
}

export const enabledFieldTypes: string[] = [
    FIELD_TYPES.SHORT_TEXT_AREA.type,
    FIELD_TYPES.LONG_TEXT_AREA.type,
    FIELD_TYPES.EMAIL.type,
    FIELD_TYPES.PHONE.type,
    FIELD_TYPES.NUMBER.type,
    FIELD_TYPES.URL.type,
    FIELD_TYPES.CURRENCY.type,
    FIELD_TYPES.ADDRESS.type,
    FIELD_TYPES.BARCODE.type,
    FIELD_TYPES.PICKLIST.type,
    FIELD_TYPES.MULTI_PICKLIST.type
];

@Component({
    selector: 'ah-dual-input',
    templateUrl: './dual-input.component.html',
    styleUrls: ['dual-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DualInputComponent implements OnInit, OnChanges {
    @Input() autoFocus? = false;
    @Input() autoSize? = true;
    @Input() field: Field;
    @Input() filter? = true;
    @Input() group: UntypedFormGroup;
    @Input() optionsLeft?: PDropdownOption[] = [];
    @Input() optionsRight?: PDropdownOption[] = [];
    @Input() placeholder?: string = null;
    @Input() required?: boolean;
    @Input() combineAsset: any[] = [];
    @Output() changeLeft? = new EventEmitter();
    @Output() changeRight? = new EventEmitter();
    @Output() changeThird? = new EventEmitter();
    @Output() changeFourth? = new EventEmitter();
    @Output() changeFifth? = new EventEmitter();
    @Output() radioClick = new EventEmitter();
    enabledFieldTypes: string[] = enabledFieldTypes;
    disabledFields: string[] = [STANDARD_FIELDS.sync_id.canonical_name];
    filterTimeout: any = null;
    formPosition = FormPosition;
    radioLeft = 'checked';
    radioRight: string;
    radioThird: string;
    radioFourth: string;
    radioFive: string;
    sumTotalCostSelected = false;
    isPatchingCost = false;
    totalCost = 0;
    @Input() allFields: Field[] = [];
    disableSumTotalCost = false;
    tooltipText: string = CURRENCY_MISMATCH_TOOLTIP;

    constructor(public deviceService: DeviceDetectorService) { }

    ngOnInit() {
        this.radioLeft = `${this.field.name}${FormPosition.Left}`;
        this.radioClick.emit({ [this.field.canonical_name]: this.group.get(`${this.field.canonical_name}${FormPosition.Left}`) });

        // FormControls have an issue disabling/enabling immediately after being created
        // see (https://github.com/angular/angular/issues/22556) so for default
        // state we have to do that here OR use formBuilder.control() to create the disabled controls
        setTimeout(() => {
            for (let i = 1; i < this.combineAsset.length; i++) {
                this.group.get(`${this.field.canonical_name}${i}`).disable();
            }

        });
        this.checkCurrencyMismatch();
        this.subscribeToCostFieldChanges();
        this.updateTotalCost();
    }

    checkCurrencyMismatch(): void {
        const currencies = new Set(
            this.combineAsset
                .map(asset => asset.displayFields?.origin_currency)
                .filter(currency => currency != null)
        );
        this.disableSumTotalCost = currencies.size > 1;
    }

    isPicklistField(field: Field): boolean {
        return field.type === FIELD_TYPES.PICKLIST.type;
    }

    makePicklistOption(field: Field, position: FormPosition): PDropdownOption {
        const asset = this.combineAsset[position];
        if (field && asset) {
            const label = asset.displayFields[field.canonical_name] ? asset.displayFields[field.canonical_name] : field.name;
            const options = { label, value: asset[field.canonical_name] };
            return options;
        }
        return null;
    }

    ngOnChanges(changes) {
        // Toggling fields regroups the form, so disable the field that isn't selected
        if (changes.group && !changes.group.firstChange) {
            if (!this.radioLeft) {
                this.group.get(`${this.field.canonical_name}${this.formPosition.Left}`).disable();
            } else if (!this.radioRight) {
                this.group.get(`${this.field.canonical_name}${this.formPosition.Right}`).disable();
            }

            if (this.combineAsset.length > 2 && !this.radioThird) {
                this.group.get(`${this.field.canonical_name}${this.formPosition.Third}`).disable();
            }
            if (this.combineAsset.length > 3 && !this.radioFourth) {
                this.group.get(`${this.field.canonical_name}${this.formPosition.Fourth}`).disable();
            }
            if (this.combineAsset.length > 4 && !this.radioFive) {
                this.group.get(`${this.field.canonical_name}${this.formPosition.Fifth}`).disable();
            }
        }
    }

    handleRadioClick(position: FormPosition) {
        this.radioClick.emit({ [this.field.canonical_name]: this.group.get(`${this.field.canonical_name}${position}`) });

        if (position === FormPosition.Left) {
            this.radioLeft = `${this.field.name}${position}`;
            this.radioRight = '';
            this.radioThird = '';
            this.radioFourth = '';
            this.radioFive = '';
        } else if (position === FormPosition.Right) {
            this.radioRight = `${this.field.name}${position}`;
            this.radioLeft = '';
            this.radioThird = '';
            this.radioFourth = '';
            this.radioFive = '';
        }
        if (this.combineAsset.length > 2 && position === FormPosition.Third) {
            this.radioThird = `${this.field.name}${position}`;
            this.radioLeft = '';
            this.radioRight = '';
            this.radioFourth = '';
            this.radioFive = '';
        }
        if (this.combineAsset.length > 3 && position === FormPosition.Fourth) {
            this.radioFourth = `${this.field.name}${position}`;
            this.radioThird = '';
            this.radioLeft = '';
            this.radioRight = ''
            this.radioFive = '';
        }
        if (this.combineAsset.length > 4 && position === FormPosition.Fifth) {
            this.radioFourth = '';
            this.radioThird = '';
            this.radioLeft = '';
            this.radioRight = ''
            this.radioFive = `${this.field.name}${position}`;
        }

        if (this.enabledFieldTypes.includes(this.field.type) && !this.disabledFields.includes(this.field.canonical_name)) {
            for (let i = 0; i < this.combineAsset.length; i++) {
                this.group.get(`${this.field.canonical_name}${i}`).disable();
                if (i === position) {
                    this.group.get(`${this.field.canonical_name}${position}`).enable();
                }
            }
        }
    }

    isControlDisabled(canonicalName: string, position: FormPosition) {
        const control: AbstractControl = this.group.get(`${canonicalName}${position}`);

        if (control) {
            return control.disabled;
        }

        return;
    }

    onChangeLeft(changeEvent) {
        if (this.filterTimeout) {
            clearTimeout(this.filterTimeout);
        }

        this.filterTimeout = setTimeout(() => {
            this.changeLeft.emit({ [this.field.canonical_name]: this.group.get(`${this.field.canonical_name}${FormPosition.Left}`) });
        }, 750);
    }

    onChangeRight(changeEvent) {
        if (this.filterTimeout) {
            clearTimeout(this.filterTimeout);
        }

        this.filterTimeout = setTimeout(() => {
            this.changeRight.emit({ [this.field.canonical_name]: this.group.get(`${this.field.canonical_name}${FormPosition.Right}`) });
        }, 750);
    }
    onChangeThird(changeEvent) {
        if (this.filterTimeout) {
            clearTimeout(this.filterTimeout);
        }

        this.filterTimeout = setTimeout(() => {
            this.changeThird.emit({ [this.field.canonical_name]: this.group.get(`${this.field.canonical_name}${FormPosition.Third}`) });
        }, 750);
    }
    onChangeFourth(changeEvent) {
        if (this.filterTimeout) {
            clearTimeout(this.filterTimeout);
        }

        this.filterTimeout = setTimeout(() => {
            this.changeFourth.emit({ [this.field.canonical_name]: this.group.get(`${this.field.canonical_name}${FormPosition.Fourth}`) });
        }, 750);
    }
    onChangeFifth(changeEvent) {
        if (this.filterTimeout) {
            clearTimeout(this.filterTimeout);
        }

        this.filterTimeout = setTimeout(() => {
            this.changeFifth.emit({ [this.field.canonical_name]: this.group.get(`${this.field.canonical_name}${FormPosition.Fifth}`) });
        }, 750);
    }

    onSelectRowCheckbox(check: any) {
        if (check.checked === false) {
            this.sumTotalCostSelected = false;
            this.patchAllOriginCostFields('0.00', true);
        } else {
            this.sumTotalCostSelected = true;
            this.updateTotalCost();
            this.patchAllOriginCostFields(Number(this.totalCost).toFixed(2));
        }
    }

    valueChange(field: any, selectedField: any) {
        if (selectedField.canonical_name === 'cost') {
            this.sumTotalCostSelected = field.target.value === this.totalCost ? true : false;
        }
    }

    updateTotalCost(): void {
        this.totalCost = this.combineAsset
            .map(asset => Number(asset.cost) || 0)
            .reduce((sum, cost) => sum + cost, 0);
    }

    patchAllOriginCostFields(value: string | number, revertToOriginal: boolean = false) {
        this.isPatchingCost = true;
        this.allFields
            .filter(field => field.canonical_name === 'cost')
            .forEach(field => {
                for (let i = 0; i < this.combineAsset.length; i++) {
                    const controlName = `${field.canonical_name}${i}`;
                    if (this.group.get(controlName)) {
                        if (revertToOriginal) {
                            const originalCost = this.combineAsset[i]?.cost ?? '0.00';
                            this.group.get(controlName).patchValue(Number(originalCost).toFixed(2));
                        } else {
                            this.group.get(controlName).patchValue(value);
                        }
                    }
                }
            });
        this.isPatchingCost = false;
    }

    //Manually editing any origin cost field will uncheck the Sum Total Cost Selected checkbox
    subscribeToCostFieldChanges() {
        this.allFields
            .filter(field => field.canonical_name === 'cost')
            .forEach(field => {
                for (let i = 0; i < this.combineAsset.length; i++) {
                    const controlName = `${field.canonical_name}${i}`;
                    const control = this.group.get(controlName);
                    if (control) {
                        control.valueChanges.subscribe((val) => {
                            // Only uncheck if not patching programmatically
                            if (this.sumTotalCostSelected && !this.isPatchingCost) {
                                this.sumTotalCostSelected = false;
                            }
                        });
                    }
                }
            });
    }
}
